import React, { useState, useEffect } from 'react'
import { ActivityIndicator, ViewStyle, TextStyle } from 'react-native'
import { InputAdornment, TextField } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { BLOCKED_FILE_EXTENSIONS, useStores } from '@trivie/core'
import { Text } from '@trivie/ui-mobile/components/text'
import { Button, color } from '@trivie/ui-mobile'
import { Avatar } from '@trivie/ui-web/components/avatar'
import { AlertDialog } from '@trivie/ui-web/components/alert-dialog'
import { AddPhotoAlternateRounded } from '@mui/icons-material'
import { Controller, useForm } from 'react-hook-form'
import { PersonRounded } from '@mui/icons-material'

export const TestLogin = observer(() => {
  const { userStore } = useStores()
  const { login, getUser, workspaceVerify, user, uploadProfilePhoto, updateUser } = userStore

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [password, setPassword] = useState('')
  const [employeeID, setEmployeeID] = useState('')
  const [showFirstLast, setShowFirstLast] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [workspace, setWorkspace] = useState<any>(null)

  const history = useHistory()

  const { control } = useForm({ shouldUnregister: false })

  const match = useRouteMatch<any>('/login/workspace/:access_code')
  const code = String(match?.params?.access_code)

  useEffect(() => {
    workspaceVerify(code).then((result) => {
      setWorkspace(result)
    })
  }, [])

  const handleLogin = () => {
    login({ username: employeeID, password }, workspace?.access_code).then((res) => {
      if (res) {
        getUser(res).then((data) => {
          if (!data?.first_name || !data?.last_name) {
            setShowFirstLast(true)
          } else {
            history.replace('/library')
          }
        })
      }
    })
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100vw',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {showFirstLast ? (
        <div style={CONTENT}>
          <div style={HEADER}>
            <Text variant="titleEmphasized" style={SIGN_IN} text="Almost There..." />
          </div>
          <AlertDialog
            open={!!errorMessage}
            title="Upload Failed"
            message={errorMessage || ''}
            buttonText="Close"
            onClose={() => {
              setErrorMessage('')
            }}
          />
          <div style={{ position: 'relative', marginBottom: 20 }}>
            {!userStore?.user?.photo_url && (
              <AddPhotoAlternateRounded
                htmlColor={color.black}
                style={{
                  fontSize: 24,
                  zIndex: 999,
                  position: 'absolute',
                  top: 32,
                  left: 35,
                }}
              />
            )}
            {userStore?.isLoading ? (
              <ActivityIndicator />
            ) : (
              <Controller
                control={control}
                render={({ value }) => (
                  <Avatar
                    size={90}
                    name={`${firstName} ${lastName}`}
                    url={value ? URL.createObjectURL(value) : user?.photo_url}
                    onChange={(file: File) => {
                      if (file) {
                        if (BLOCKED_FILE_EXTENSIONS.includes(file.type)) {
                          let targetStr = file.type
                          if (targetStr.includes('/')) {
                            targetStr = targetStr.split('/')[1]
                            setErrorMessage(`Sorry, but we don't support ${targetStr} images`)
                          } else {
                            setErrorMessage(`Sorry, but we don't support ${file.type} files`)
                          }
                        } else {
                          const formData = new FormData()
                          formData.append('photo', file)
                          uploadProfilePhoto(formData, user!.id)
                        }
                      }
                    }}
                  />
                )}
                name="photo"
                defaultValue=""
              />
            )}
          </div>
          <TextField
            variant="standard"
            required
            onChange={(e: any) => setFirstName(e.target.value)}
            style={{ marginTop: 60, width: 335, marginBottom: 51 }}
            size="small"
            InputProps={{
              style: { paddingBottom: 8 },
              startAdornment: (
                <InputAdornment position="start">
                  <PersonRounded htmlColor={color.black} />
                </InputAdornment>
              ),
            }}
            placeholder="Your First Name"
          />
          <TextField
            variant="standard"
            required
            onChange={(e: any) => setLastName(e.target.value)}
            style={{ width: 335, marginBottom: 51 }}
            size="small"
            InputProps={{
              style: { paddingBottom: 8 },
              startAdornment: (
                <InputAdornment position="start">
                  <PersonRounded htmlColor={color.black} />
                </InputAdornment>
              ),
            }}
            placeholder="Your Last Name"
          />
          <Button
            accessibilityRole="button"
            accessibilityLabel="Create Account"
            disabled={!firstName || !lastName}
            variant="secondary"
            style={{
              ...SUBMIT,
              marginBottom: 50,
              backgroundColor: !firstName || !lastName ? color.shade20 : color.purple,
            }}
            tx="Onboarding.SignUp.createAccount"
            onPress={() => {
              updateUser({ first_name: firstName, last_name: lastName })
              history.replace('/library')
            }}
          />
        </div>
      ) : (
        <div style={CONTENT}>
          <div style={HEADER}>
            <Text variant="titleEmphasized" style={SIGN_IN} tx="Onboarding.Login.secureLogin" />
            <Text
              variant="titleMedium"
              style={{ color: color.shade70, textAlign: 'center', marginBottom: 60 }}
              tx="Onboarding.Login.employeeID"
            />
            <img
              src={workspace?.logo_path}
              style={{ height: 100, width: 120, alignSelf: 'center', marginBottom: 60 }}
            />
          </div>
          <TextField
            variant="standard"
            value={employeeID}
            style={{ width: 335, marginBottom: 51 }}
            placeholder="Employee ID"
            onChange={(e) => setEmployeeID(e.target.value)}
          />
          <TextField
            variant="standard"
            type="password"
            placeholder="Password"
            value={password}
            style={{ width: 335, marginBottom: 51 }}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button
            disabled={!employeeID || !password}
            style={SUBMIT}
            tx="Common.continue"
            onPress={handleLogin}
            accessibilityRole="button"
            accessibilityLabel="Send Magic Link"
          />
        </div>
      )}
    </div>
  )
})

const SUBMIT: ViewStyle = {
  marginHorizontal: 20,
  width: 335,
  alignSelf: 'center',
}

const CONTENT: React.CSSProperties = {
  height: '100%',
  flex: 1,
  backgroundColor: '#fff',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  alignSelf: 'center',
}
const HEADER: React.CSSProperties = {
  minWidth: 319,
  flexDirection: 'column',
  display: 'flex',
}
const TAGLINE: TextStyle = {
  color: color.black,
  textAlign: 'center',
}
const HEADLINE: TextStyle = { textAlign: 'center', marginBottom: 12 }

const SIGN_IN: TextStyle = {
  fontWeight: '600',
  fontSize: 24,
  lineHeight: 28,
  textAlign: 'center',
  marginTop: 40,
  marginBottom: 24,
  width: '100%',
}
